// src/lib/api/financial/expenses.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, validatePositiveNumber, sanitizeInput } from '../shared/validation'
import { triggerQuickBooksSync } from '../../quickbooksSync'
import { buildExpenseDescription } from '@prosperia/shared/utils'

/**
 * List expenses with optional filtering
 * Migrated from supabaseApi.js lines 875-895
 */
const _listExpenses = async (options = {}) => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }
  
  let query = supabase
    .from('expenses')
    .select('id, expense_date, vendor, total_amount, status, created_at, category_id, subcategory_id, is_split, is_billable, is_reimbursable')
    .eq('company_id', companyId)
    .order('expense_date', { ascending: false })

  if (Array.isArray(options.status) && options.status.length > 0) {
    query = query.in('status', options.status)
  }

  const { data, error } = await query
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new expense
 * Migrated from supabaseApi.js lines 897-925
 */
const _createExpense = async (expense) => {
  validateRequired(expense.vendor, 'Vendor')
  validateRequired(expense.total_amount, 'Total amount')
  validatePositiveNumber(expense.total_amount, 'Total amount')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const userId = await getValidatedUserId()
  if (!userId) throw new Error('Not authenticated')

  const payload = {
    company_id: companyId,
    created_by: userId,
    // Legacy NOT NULL columns with no default (#587)
    description: buildExpenseDescription(expense),
    amount: expense.total_amount,
    vendor: sanitizeInput(expense.vendor),
    expense_date: expense.expense_date,
    total_amount: expense.total_amount,
    tax_amount: expense.tax_amount ?? 0,
    currency: expense.currency || 'USD',
    category_id: expense.category_id,
    subcategory_id: expense.subcategory_id || null,
    payment_method: expense.payment_method,
    notes: expense.notes ? sanitizeInput(expense.notes) : null,
    job_id: expense.job_id || null,
    is_billable: !!expense.is_billable,
    is_reimbursable: !!expense.is_reimbursable,
    client_id: expense.client_id || null,
    billable_description: expense.billable_description ? sanitizeInput(expense.billable_description) : null,
    status: expense.status || 'posted'
  }

  const { data, error } = await supabase
    .from('expenses')
    .insert([payload])
    .select('id')
    .single()

  if (error) throw error
  triggerQuickBooksSync('expense', data?.id)
  return data
}

/**
 * Update an expense
 */
const _updateExpense = async (expenseId, expenseData) => {
  validateRequired(expenseId, 'Expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  if (expenseData.total_amount) {
    validatePositiveNumber(expenseData.total_amount, 'Total amount')
  }
  
  const sanitizedData = {
    ...expenseData,
    updated_at: new Date().toISOString()
  }
  
  if (sanitizedData.vendor) {
    sanitizedData.vendor = sanitizeInput(sanitizedData.vendor)
  }
  if (sanitizedData.notes) {
    sanitizedData.notes = sanitizeInput(sanitizedData.notes)
  }
  
  const { data, error } = await supabase
    .from('expenses')
    .update(sanitizedData)
    .eq('id', expenseId)
    .eq('company_id', companyId)
    .select()
    .single()

  if (error) throw error
  triggerQuickBooksSync('expense', expenseId)
  return data
}

/**
 * Delete an expense
 */
const _deleteExpense = async (expenseId) => {
  validateRequired(expenseId, 'Expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { error } = await supabase
    .from('expenses')
    .delete()
    .eq('id', expenseId)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get expense by ID
 */
const _getExpenseById = async (expenseId) => {
  validateRequired(expenseId, 'Expense ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { data, error } = await supabase
    .from('expenses')
    .select(`
      *,
      category:expense_categories(name, type),
      job:jobs(title)
    `)
    .eq('id', expenseId)
    .eq('company_id', companyId)
    .single()
  
  if (error) throw error
  return data
}

/**
 * List expense categories
 * Migrated from supabaseApi.js lines 927-940
 */
const _listExpenseCategories = async () => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) return createSuccessResponse([])
  
  const { data, error } = await supabase
    .from('expense_categories')
    .select('id, name, parent_id, type')
    .eq('company_id', companyId)
    .order('name', { ascending: true })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create expense category
 * Migrated from supabaseApi.js lines 942-950
 */
const _createExpenseCategory = async ({ name, type, parent_id = null }) => {
  validateRequired(name, 'Category name')
  validateRequired(type, 'Category type')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { data, error } = await supabase
    .from('expense_categories')
    .insert([{ 
      company_id: companyId, 
      name: sanitizeInput(name), 
      type, 
      parent_id 
    }])
    .select('id, name, parent_id, type')
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update expense category
 * Migrated from supabaseApi.js lines 952-965
 */
const _updateExpenseCategory = async (id, { name, parent_id, type }) => {
  validateRequired(id, 'Category ID')
  validateRequired(name, 'Category name')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { data, error } = await supabase
    .from('expense_categories')
    .update({ 
      name: sanitizeInput(name), 
      parent_id, 
      type, 
      updated_at: new Date().toISOString() 
    })
    .eq('id', id)
    .eq('company_id', companyId)
    .select('id, name, parent_id, type')
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete expense category
 * Migrated from supabaseApi.js lines 967-975
 */
const _deleteExpenseCategory = async (id) => {
  validateRequired(id, 'Category ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')
  
  const { error } = await supabase
    .from('expense_categories')
    .delete()
    .eq('id', id)
    .eq('company_id', companyId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get job costs by category
 * Migrated from supabaseApi.js lines 1000-1015
 */
const _getJobCostsByCategory = async (jobId) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) return createSuccessResponse({})
  
  const { data, error } = await supabase
    .from('expenses')
    .select('category_id, total_amount')
    .eq('company_id', companyId)
    .eq('job_id', jobId)
  
  if (error) throw error
  
  const sums = {}
  for (const row of (data || [])) {
    sums[row.category_id] = (sums[row.category_id] || 0) + Number(row.total_amount || 0)
  }
  
  return createSuccessResponse(sums)
}

/**
 * Get job financial summary
 * Migrated from supabaseApi.js lines 1017-1035
 */
const _getJobFinancialSummary = async (jobId) => {
  validateRequired(jobId, 'Job ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) return createSuccessResponse(null)
  
  const { data, error } = await supabase
    .from('job_financial_summary_view')
    .select('total_job_costs, total_invoiced_amount')
    .eq('company_id', companyId)
    .eq('job_id', jobId)
    .maybeSingle()
  
  if (error) throw error
  
  const totalCosts = Number(data?.total_job_costs || 0)
  const totalInvoiced = Number(data?.total_invoiced_amount || 0)
  const profit = totalInvoiced - totalCosts
  const margin = totalInvoiced > 0 ? profit / totalInvoiced : null
  
  return createSuccessResponse({ totalInvoiced, totalCosts, profit, margin })
}

/**
 * Bulk update expense category for multiple expenses
 * @param {string[]} expenseIds - Array of expense IDs to update
 * @param {string} categoryId - New category ID
 * @param {string|null} subcategoryId - Optional new subcategory ID
 * @returns {object} Count of updated records
 */
const _bulkUpdateExpenseCategory = async (expenseIds, categoryId, subcategoryId = null) => {
  validateRequired(expenseIds, 'Expense IDs')
  validateRequired(categoryId, 'Category ID')

  if (!Array.isArray(expenseIds) || expenseIds.length === 0) {
    throw new Error('At least one expense ID is required')
  }

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const updatePayload = {
    category_id: categoryId,
    subcategory_id: subcategoryId || null,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('expenses')
    .update(updatePayload)
    .in('id', expenseIds)
    .eq('company_id', companyId)
    .select('id')

  if (error) throw error
  return { count: data?.length || 0 }
}

/**
 * Bulk delete expenses
 * @param {string[]} expenseIds - Array of expense IDs to delete
 * @returns {object} Success status
 */
const _bulkDeleteExpenses = async (expenseIds) => {
  validateRequired(expenseIds, 'Expense IDs')

  if (!Array.isArray(expenseIds) || expenseIds.length === 0) {
    throw new Error('At least one expense ID is required')
  }

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  const { error } = await supabase
    .from('expenses')
    .delete()
    .in('id', expenseIds)
    .eq('company_id', companyId)

  if (error) throw error
  return { success: true, count: expenseIds.length }
}

/**
 * Get all splits for an expense
 * @param {string} expenseId - The expense ID
 * @returns {object[]} Array of split records
 */
const _getExpenseSplits = async (expenseId) => {
  validateRequired(expenseId, 'Expense ID')

  const { data, error } = await supabase
    .from('expense_splits')
    .select('id, expense_id, category_id, amount, description, created_at')
    .eq('expense_id', expenseId)
    .order('created_at', { ascending: true })

  if (error) throw error
  return data || []
}

/**
 * Save expense splits - replaces all existing splits for an expense
 * Validates that total of splits equals expense total_amount
 * @param {string} expenseId - The expense ID
 * @param {object[]} splits - Array of split objects { category_id, amount, description }
 * @returns {object[]} Created split records
 */
const _saveExpenseSplits = async (expenseId, splits) => {
  validateRequired(expenseId, 'Expense ID')

  if (!Array.isArray(splits) || splits.length === 0) {
    throw new Error('At least one split is required')
  }

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  // Verify the expense belongs to this company and get its total
  const { data: expense, error: expenseError } = await supabase
    .from('expenses')
    .select('id, total_amount')
    .eq('id', expenseId)
    .eq('company_id', companyId)
    .single()

  if (expenseError) throw expenseError
  if (!expense) throw new Error('Expense not found')

  // Validate split amounts sum to expense total
  const splitTotal = splits.reduce((sum, s) => sum + Number(s.amount || 0), 0)
  const expenseTotal = Number(expense.total_amount)

  if (Math.abs(splitTotal - expenseTotal) > 0.01) {
    throw new Error(`Split amounts ($${splitTotal.toFixed(2)}) must equal expense total ($${expenseTotal.toFixed(2)})`)
  }

  // Delete existing splits
  const { error: deleteError } = await supabase
    .from('expense_splits')
    .delete()
    .eq('expense_id', expenseId)

  if (deleteError) throw deleteError

  // Insert new splits
  const splitPayloads = splits.map(s => ({
    expense_id: expenseId,
    category_id: s.category_id,
    amount: Number(s.amount),
    description: s.description ? sanitizeInput(s.description) : null
  }))

  const { data: createdSplits, error: insertError } = await supabase
    .from('expense_splits')
    .insert(splitPayloads)
    .select('id, expense_id, category_id, amount, description, created_at')

  if (insertError) throw insertError

  // Mark expense as split and update primary category to first split's category
  const { error: updateError } = await supabase
    .from('expenses')
    .update({
      is_split: true,
      category_id: splits[0].category_id,
      updated_at: new Date().toISOString()
    })
    .eq('id', expenseId)
    .eq('company_id', companyId)

  if (updateError) throw updateError

  return createdSplits || []
}

/**
 * Remove all splits from an expense and reset is_split flag
 * @param {string} expenseId - The expense ID
 * @returns {object} Success status
 */
const _removeExpenseSplits = async (expenseId) => {
  validateRequired(expenseId, 'Expense ID')

  const companyId = await getValidatedCompanyId()
  if (!companyId) throw new Error('No company ID found')

  // Delete all splits
  const { error: deleteError } = await supabase
    .from('expense_splits')
    .delete()
    .eq('expense_id', expenseId)

  if (deleteError) throw deleteError

  // Reset is_split flag
  const { error: updateError } = await supabase
    .from('expenses')
    .update({
      is_split: false,
      updated_at: new Date().toISOString()
    })
    .eq('id', expenseId)
    .eq('company_id', companyId)

  if (updateError) throw updateError

  return { success: true }
}

// Export wrapped functions
export const listExpenses = withErrorHandling(_listExpenses, 'list expenses', 'expenses')
export const createExpense = withErrorHandling(_createExpense, 'create expense', 'expense')
export const updateExpense = withErrorHandling(_updateExpense, 'update expense', 'expense')
export const deleteExpense = withErrorHandling(_deleteExpense, 'delete expense', 'expense')
export const getExpenseById = withErrorHandling(_getExpenseById, 'get expense by ID', 'expense')
export const listExpenseCategories = withErrorHandling(_listExpenseCategories, 'list expense categories', 'expense_categories')
export const createExpenseCategory = withErrorHandling(_createExpenseCategory, 'create expense category', 'expense_category')
export const updateExpenseCategory = withErrorHandling(_updateExpenseCategory, 'update expense category', 'expense_category')
export const deleteExpenseCategory = withErrorHandling(_deleteExpenseCategory, 'delete expense category', 'expense_category')
export const getJobCostsByCategory = withErrorHandling(_getJobCostsByCategory, 'get job costs by category', 'expenses')
export const getJobFinancialSummary = withErrorHandling(_getJobFinancialSummary, 'get job financial summary', 'job_financials')
export const bulkUpdateExpenseCategory = withErrorHandling(_bulkUpdateExpenseCategory, 'bulk update expense category', 'expenses')
export const bulkDeleteExpenses = withErrorHandling(_bulkDeleteExpenses, 'bulk delete expenses', 'expenses')
export const getExpenseSplits = withErrorHandling(_getExpenseSplits, 'get expense splits', 'expense_splits')
export const saveExpenseSplits = withErrorHandling(_saveExpenseSplits, 'save expense splits', 'expense_splits')
export const removeExpenseSplits = withErrorHandling(_removeExpenseSplits, 'remove expense splits', 'expense_splits')